import { Suspense, lazy, useEffect, useMemo } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  useSidebar,
} from "../ui/sidebar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tab";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";

const FileList = lazy(() => import("../modules/files/file-list"));
const PatientTabContent = lazy(() => import("../modules/patients/patient-tab"));
const ChatList = lazy(() => import("../modules/chat/chat-list"));
import {
  selectCurrentRoom,
  setActiveTab,
  setFocusOnChats,
} from "@/context/features/room/roomSlice";
import { useDispatch, useSelector } from "react-redux";
const ChatControll = lazy(() => import("../modules/chat/chat-controll"));
import RoleOnly from "@/components/elements/role-only";
import { Button } from "../ui/button";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

export default function SidebarTemplate() {
  // state
  const { focusOnChats, activeTab } = useSelector(selectCurrentRoom);
  const { setOpen, isMobile, setOpenMobile, open, openMobile } = useSidebar();

  // hooks
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (focusOnChats) {
      setOpen(true);
      if (isMobile) {
        setOpenMobile(true);
      } else {
        setOpen(true);
      }
      dispatch(setActiveTab("chats"));
      dispatch(setFocusOnChats(false));
    }
  }, [focusOnChats, isMobile, setOpen, setOpenMobile, dispatch]);

  useEffect(() => {
    if (focusOnChats) {
      setOpen(true);
      dispatch(setActiveTab("chats"));
    }
  }, [focusOnChats, setOpen, dispatch]);

  // render
  const memoizedFileList = useMemo(
    () => (
      <Suspense fallback={null}>
        <FileList />
      </Suspense>
    ),
    []
  );

  const memoizedPatient = useMemo(
    () => (
      <Suspense fallback={null}>
        <PatientTabContent />
      </Suspense>
    ),
    []
  );

  return (
    <Sidebar>
      <SidebarHeader className="bg-background border-b">
        <div className="flex items-center justify-between gap-2 pe-3 h-[39px]">
          {isMobile ? (
            <Button
              variant="outline"
              size={"sm"}
              className="gap-2"
              onClick={() => {
                setOpen(false);
                if (isMobile) setOpenMobile(false);
              }}
            >
              <ArrowRight className="text-primary size-5" />
              {t("return")}
            </Button>
          ) : (
            <div></div>
          )}
          <Button
            variant="ghost"
            size={"sm"}
            className="gap-2"
            onClick={() => {
              setOpen(false);
              if (isMobile) setOpenMobile(false);
            }}
          >
            <X className="text-primary size-5" />
          </Button>
        </div>
      </SidebarHeader>
      {(open || openMobile) && (
        <SidebarContent className="h-dvh overflow-hidden flex flex-col gap-0">
          <Tabs
            value={activeTab}
            onValueChange={(value) =>
              dispatch(
                setActiveTab(value as "patients_list" | "chats" | "files")
              )
            }
            defaultValue="patients_list"
            className="relative grow w-full flex items-center bg-background pt-3 overflow-hidden"
          >
            <TabsList>
              <RoleOnly>
                <TabsTrigger className="w-24" value="patients_list">
                  {t("patients_list")}
                </TabsTrigger>
              </RoleOnly>

              <TabsTrigger className="w-24" value="chats">
                {t("chats")}
              </TabsTrigger>
              <TabsTrigger className="w-24" value="files">
                {t("files")}
              </TabsTrigger>
            </TabsList>
            <TabsContent
              value="files"
              dir={i18n.dir()}
              className={cn(
                "w-full flex-1 flex flex-col overflow-hidden",
                activeTab === "files" ? "" : "hidden"
              )}
            >
              {memoizedFileList}
            </TabsContent>
            <RoleOnly>
              <TabsContent
                value="patients_list"
                dir={i18n.dir()}
                className={cn(
                  "w-full flex-1 flex flex-col overflow-hidden",
                  activeTab === "patients_list" ? "" : "hidden"
                )}
              >
                {memoizedPatient}
              </TabsContent>
            </RoleOnly>
            <TabsContent
              dir={i18n.dir()}
              value="chats"
              className={cn(
                "border-t w-full flex-1 flex flex-col overflow-hidden ",
                activeTab === "chats" ? "" : "hidden"
              )}
            >
              <Suspense fallback={null}>
                <RoleOnly fallback={() => <ChatList />}>
                  <ChatControll activeTab={activeTab} />
                </RoleOnly>
              </Suspense>
            </TabsContent>
          </Tabs>
        </SidebarContent>
      )}
    </Sidebar>
  );
}
